// @givmo/assistant-panel — the top-bar entry control.
//
// A LABELLED ghost button, not an icon. The two controls beside it in a typical top bar (help, the
// account avatar) are icon-only because everyone already knows what they are; an assistant is a
// capability nobody is looking for yet, and an unlabelled glyph makes it invisible on the one visit
// that matters — the first. It stays visually the lightest thing in the bar (no fill, secondary text)
// so being findable costs no prominence.
//
// It is the SaaS ghost button's own recipe rather than a `<Button>` because the design-system button
// puts its icon in a slot with the kit's own gap, and this control's glyph-to-word relationship is
// tighter than that. The height, radius, weight and colour are the ghost `md` values.
//
// ONE DENSITY CHOICE IS THE HOST'S (`glyphOnPhone`): a bar that must keep its other words on a phone
// takes the glyph alone below the phone breakpoint, and the label stays the control's accessible
// name there — visually hidden, still read. From the breakpoint up it is the labelled control above.

import type { Ref } from 'react';
import { Icon, testIdProp } from '@givmo/design-system';
import { Sparkles } from 'lucide-react';

/** The labelled ghost `md` recipe — every width, unless the host takes the phone glyph. */
const LABELLED =
  'inline-flex h-36 shrink-0 items-center gap-6 rounded-md px-14 font-sans text-s14 font-w600 tracking-givmo text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text-strong focus-visible:shadow-ring';

/**
 * Below the phone breakpoint: the 44px square every control beside it takes there, the label hidden.
 * APPENDED, never merged through `cn`: the kit's merger reads `font-w600` as a font FAMILY and drops
 * `font-sans`, so a merged recipe would not be the labelled control class for class.
 */
const PHONE_GLYPH = 'max-sm:size-44 max-sm:justify-center max-sm:px-0';

export interface AssistantEntryButtonProps {
  /** The assistant's user-facing name — the whole label. */
  label: string;
  onClick: () => void;
  /** Whether the panel it opens is currently showing. */
  expanded: boolean;
  testId: string;
  /**
   * Below the phone breakpoint (`sm`), draw the glyph alone in a 44px square — the phone touch
   * target of the controls beside it — with the label kept as the accessible name. Off (the
   * default), the control is labelled at every width.
   */
  glyphOnPhone?: boolean;
  /** The control itself, so a docked panel that closes can hand focus back to it. */
  ref?: Ref<HTMLButtonElement>;
}

export function AssistantEntryButton({
  label,
  onClick,
  expanded,
  testId,
  glyphOnPhone = false,
  ref,
}: AssistantEntryButtonProps) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className={glyphOnPhone ? `${LABELLED} ${PHONE_GLYPH}` : LABELLED}
      {...testIdProp(testId)}
    >
      <Icon icon={Sparkles} size={16} className="text-icon" aria-hidden />
      <span className={glyphOnPhone ? 'whitespace-nowrap max-sm:sr-only' : 'whitespace-nowrap'}>
        {label}
      </span>
    </button>
  );
}
